import { GoogleGenerativeAI } from '@google/generative-ai';

// Inicializar Gemini AI
const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);

/**
 * Analiza una transcripción de reunión y extrae información estructurada
 * @param {string} transcription - Texto de la transcripción de la reunión
 * @returns {Promise<Object>} - Objeto con los datos extraídos para el formulario
 */
export const analyzeTranscription = async (transcription) => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-3.1-flash-lite' });
    
    const prompt = `
Analiza la siguiente transcripción de una reunión y extrae la información relevante para completar un acta de reunión. 
Devuelve ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "tipoActa": "string - tipo de acta (ej: 'Reunión del contrato o convenio', 'Comité técnico', etc.)",
  "fecha": "string - fecha en formato YYYY-MM-DD si se menciona",
  "horaInicio": "string - hora de inicio en formato HH:MM si se menciona",
  "horaFinal": "string - hora de finalización en formato HH:MM si se menciona",
  "lugar": "string - lugar de la reunión (presencial, virtual, híbrido)",
  "objetivo": "string - objetivo principal de la reunión",
  "ordenDia": [
    {
      "titulo": "string - título del punto",
      "descripcion": "string - descripción del punto tratado"
    }
  ],
  "compromisos": [
    {
      "compromiso": "string - descripción del compromiso",
      "responsable": "string - persona responsable",
      "fechaCumplimiento": "string - fecha límite en formato YYYY-MM-DD si se menciona"
    }
  ],
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se menciona",
      "asistio": "string - 'Presencial', 'Virtual' o 'No asistió'"
    }
  ],
  "responsable": "string - persona que lidera o modera la reunión"
}

INSTRUCCIONES IMPORTANTES PARA ASISTENTES:
- Si en la transcripción aparecen nombres de personas con formato de timestamp y dos puntos (ej: "[10:01 a. m.] Laura Sánchez Ramírez:" o "Juan Pérez: Estoy de acuerdo"), esto indica que esa persona está participando virtualmente.
- Si una persona habla pero su nombre no aparece explícitamente como etiqueta en el texto, asume que está presente físicamente.
- Para participantes virtuales, marca "asistio": "Virtual"
- Para participantes presenciales, marca "asistio": "Presencial"
- Si no puedes determinar el modo de participación, usa "Presencial" por defecto.

Si algún campo no se puede determinar de la transcripción, déjalo como string vacío "" o array vacío [].

Transcripción a analizar:
${transcription}
`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    // Limpiar el texto para extraer solo el JSON
    let jsonText = text.trim();
    
    // Remover posibles marcadores de código
    jsonText = jsonText.replace(/```json\s*/, '').replace(/```\s*$/, '');
    
    // Intentar parsear el JSON
    try {
      const parsedData = JSON.parse(jsonText);
      
      // Validar y limpiar los datos
      return validateAndCleanData(parsedData);
    } catch (parseError) {
      console.error('Error parsing JSON from Gemini response:', parseError);
      throw new Error('La respuesta de la IA no tiene el formato esperado');
    }
    
  } catch (error) {
    console.error('Error analyzing transcription with Gemini:', error);
    
    // Detectar si la API está saturada
    if (error.message && error.message.includes('overloaded')) {
      throw new Error('🤖 La IA está temporalmente saturada. Por favor, intenta nuevamente en unos momentos. No es un error del sistema.');
    }
    
    // Detectar errores de cuota o límite de uso
    if (error.message && (error.message.includes('quota') || error.message.includes('limit'))) {
      throw new Error('🚫 Se ha alcanzado el límite de uso de la IA. Por favor, intenta más tarde.');
    }
    
    // Detectar errores de conexión
    if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
      throw new Error('🌐 Error de conexión con la IA. Verifica tu conexión a internet e intenta nuevamente.');
    }
    
    // Error genérico
    throw new Error('Error al analizar la transcripción: ' + error.message);
  }
};

/**
 * Valida y limpia los datos extraídos por Gemini
 * @param {Object} data - Datos extraídos
 * @returns {Object} - Datos validados y limpiados
 */
const validateAndCleanData = (data) => {
  const cleanData = {
    tipoActa: data.tipoActa || '',
    fecha: data.fecha || '',
    horaInicio: data.horaInicio || '',
    horaFinal: data.horaFinal || '',
    lugar: data.lugar || '',
    objetivo: data.objetivo || '',
    ordenDia: Array.isArray(data.ordenDia) ? data.ordenDia.map(item => ({
      titulo: item.titulo || '',
      descripcion: item.descripcion || ''
    })) : [],
    compromisos: Array.isArray(data.compromisos) ? data.compromisos.map(item => ({
      compromiso: item.compromiso || '',
      responsable: item.responsable || '',
      fechaCumplimiento: item.fechaCumplimiento || ''
    })) : [],
    asistentes: Array.isArray(data.asistentes) ? data.asistentes.map(item => ({
      nombre: item.nombre || '',
      cargo: item.cargo || '',
      asistio: item.asistio || 'Presencial'
    })) : [],
    responsable: data.responsable || ''
  };
  
  return cleanData;
};

/**
 * Convierte fechas y horas del formato de string a objetos Date
 * @param {Object} data - Datos con fechas en string
 * @returns {Object} - Datos con fechas convertidas
 */
export const convertDatesToObjects = (data) => {
  const convertedData = { ...data };
  
  // Convertir fecha
  if (data.fecha && data.fecha !== '') {
    try {
      convertedData.fecha = new Date(data.fecha);
    } catch (error) {
      console.warn('Error converting fecha:', error);
      convertedData.fecha = new Date();
    }
  } else {
    convertedData.fecha = new Date();
  }
  
  // Convertir horas
  if (data.horaInicio && data.horaInicio !== '') {
    try {
      const [hours, minutes] = data.horaInicio.split(':');
      const horaInicio = new Date();
      horaInicio.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      convertedData.horaInicio = horaInicio;
    } catch (error) {
      console.warn('Error converting horaInicio:', error);
      convertedData.horaInicio = null;
    }
  } else {
    convertedData.horaInicio = null;
  }
  
  if (data.horaFinal && data.horaFinal !== '') {
    try {
      const [hours, minutes] = data.horaFinal.split(':');
      const horaFinal = new Date();
      horaFinal.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      convertedData.horaFinal = horaFinal;
    } catch (error) {
      console.warn('Error converting horaFinal:', error);
      convertedData.horaFinal = null;
    }
  } else {
    convertedData.horaFinal = null;
  }
  
  // Convertir fechas de cumplimiento de compromisos
  if (convertedData.compromisos && Array.isArray(convertedData.compromisos)) {
    convertedData.compromisos = convertedData.compromisos.map(compromiso => {
      if (compromiso.fechaCumplimiento && compromiso.fechaCumplimiento !== '') {
        try {
          return {
            ...compromiso,
            fechaCumplimiento: new Date(compromiso.fechaCumplimiento)
          };
        } catch (error) {
          console.warn('Error converting fechaCumplimiento:', error);
          return {
            ...compromiso,
            fechaCumplimiento: null
          };
        }
      }
      return {
        ...compromiso,
        fechaCumplimiento: null
      };
    });
  }
  
  return convertedData;
};

/**
 * Analiza una transcripción junto con una lista de asistencia CSV
 * @param {string} transcription - Texto de la transcripción
 * @param {string} csvContent - Contenido del archivo CSV de asistencia
 * @returns {Object} - Datos extraídos y estructurados
 */
export const analyzeTranscriptionWithAttendance = async (transcription, csvContent) => {
  try {
    const model = genAI.getGenerativeModel({ model: "gemini-3.1-flash-lite" });
    
    const prompt = `
Analiza la siguiente transcripción de una reunión junto con la lista de asistencia en formato CSV de Microsoft Teams y extrae la información relevante para completar un acta de reunión.

DEVUELVE ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "tipoActa": "string - tipo de acta (ej: 'Reunión del contrato o convenio', 'Comité técnico', etc.)",
  "fecha": "string - fecha en formato YYYY-MM-DD si se menciona",
  "horaInicio": "string - hora de inicio en formato HH:MM si se menciona",
  "horaFinal": "string - hora de finalización en formato HH:MM si se menciona",
  "lugar": "string - lugar de la reunión (presencial, virtual, híbrido)",
  "objetivo": "string - objetivo principal de la reunión",
  "ordenDia": [
    {
      "titulo": "string - título del punto",
      "descripcion": "string - descripción del punto tratado"
    }
  ],
  "compromisos": [
    {
      "compromiso": "string - descripción del compromiso",
      "responsable": "string - persona responsable",
      "fechaCumplimiento": "string - fecha límite en formato YYYY-MM-DD si se menciona"
    }
  ],
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se menciona",
      "asistio": "string - 'Presencial', 'Virtual' o 'No asistió'",
      "email": "string - correo electrónico del asistente si está disponible"
    }
  ],
  "responsable": "string - persona que lidera o modera la reunión"
}

INSTRUCCIONES IMPORTANTES PARA ASISTENTES:
- Usa PRIORITARIAMENTE la información del CSV de asistencia para determinar quién asistió a la reunión
- El CSV contiene información de participantes con sus correos electrónicos y duración de participación
- Si una persona aparece en el CSV con duración > 0, marca "asistio": "Virtual" (asumiendo que Teams es virtual)
- Si una persona habla en la transcripción pero no está en el CSV, puede ser presencial
- Relaciona los nombres de la transcripción con los del CSV usando coincidencias parciales de nombres
- Incluye el email del CSV cuando esté disponible
- Si no puedes determinar el modo de participación, usa "Virtual" por defecto para personas en el CSV

Si algún campo no se puede determinar, déjalo como string vacío "" o array vacío [].

Transcripción a analizar:
${transcription}

Lista de asistencia CSV:
${csvContent}
`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    // Limpiar el texto para extraer solo el JSON
    let jsonText = text.trim();
    
    // Remover posibles marcadores de código
    jsonText = jsonText.replace(/```json\s*/, '').replace(/```\s*$/, '');
    
    // Intentar parsear el JSON
    try {
      const parsedData = JSON.parse(jsonText);
      
      // Validar y limpiar los datos
      return validateAndCleanDataWithEmail(parsedData);
    } catch (parseError) {
      console.error('Error parsing JSON from Gemini response:', parseError);
      throw new Error('La respuesta de la IA no tiene el formato esperado');
    }
    
  } catch (error) {
    console.error('Error analyzing transcription with attendance:', error);
    
    // Detectar si la API está saturada
    if (error.message && error.message.includes('overloaded')) {
      throw new Error('🤖 La IA está temporalmente saturada. Por favor, intenta nuevamente en unos momentos. No es un error del sistema.');
    }
    
    // Detectar errores de cuota o límite de uso
    if (error.message && (error.message.includes('quota') || error.message.includes('limit'))) {
      throw new Error('🚫 Se ha alcanzado el límite de uso de la IA. Por favor, intenta más tarde.');
    }
    
    // Detectar errores de conexión
    if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
      throw new Error('🌐 Error de conexión con la IA. Verifica tu conexión a internet e intenta nuevamente.');
    }
    
    // Error genérico
    throw new Error('Error al analizar la transcripción con asistencia: ' + error.message);
  }
};

/**
 * Analiza solo una lista de asistencia CSV
 * @param {string} csvContent - Contenido del archivo CSV de asistencia
 * @returns {Object} - Datos extraídos del CSV
 */
export const analyzeCsvOnly = async (csvContent) => {
  try {
    const model = genAI.getGenerativeModel({ model: "gemini-3.1-flash-lite" });
    
    const prompt = `
Analiza la siguiente lista de asistencia en formato CSV de Microsoft Teams y extrae únicamente la información de los asistentes.

DEVUELVE ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se puede inferir del email o nombre",
      "asistio": "Virtual",
      "email": "string - correo electrónico del asistente si está disponible"
    }
  ]
}

INSTRUCCIONES IMPORTANTES:
- Extrae todos los participantes del CSV que tengan duración > 0
- Marca todos como "asistio": "Virtual" (asumiendo que Teams es virtual)
- Incluye el email cuando esté disponible en el CSV
- Si no hay cargo específico, deja el campo vacío
- Limpia y normaliza los nombres (quita caracteres extraños, espacios extra, etc.)

Lista de asistencia CSV:
${csvContent}
`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    // Limpiar el texto para extraer solo el JSON
    let jsonText = text.trim();
    
    // Remover posibles marcadores de código
    jsonText = jsonText.replace(/```json\s*/, '').replace(/```\s*$/, '');
    
    // Intentar parsear el JSON
    try {
      const parsedData = JSON.parse(jsonText);
      
      // Validar y limpiar los datos
      return validateAndCleanCsvData(parsedData);
    } catch (parseError) {
      console.error('Error parsing JSON from Gemini response:', parseError);
      throw new Error('La respuesta de la IA no tiene el formato esperado');
    }
    
  } catch (error) {
    console.error('Error analyzing CSV only:', error);
    
    // Detectar si la API está saturada
    if (error.message && error.message.includes('overloaded')) {
      throw new Error('🤖 La IA está temporalmente saturada. Por favor, intenta nuevamente en unos momentos. No es un error del sistema.');
    }
    
    // Detectar errores de cuota o límite de uso
    if (error.message && (error.message.includes('quota') || error.message.includes('limit'))) {
      throw new Error('🚫 Se ha alcanzado el límite de uso de la IA. Por favor, intenta más tarde.');
    }
    
    // Detectar errores de conexión
    if (error.message && error.message.includes('fetch')) {
      throw new Error('🌐 Error de conexión. Verifica tu conexión a internet e intenta nuevamente.');
    }
    
    throw new Error('Error inesperado al procesar el CSV: ' + error.message);
  }
};

/**
 * Valida y limpia los datos extraídos del CSV
 * @param {Object} data - Datos extraídos
 * @returns {Object} - Datos validados y limpiados
 */
const validateAndCleanCsvData = (data) => {
  const cleanData = {
    asistentes: Array.isArray(data.asistentes) ? data.asistentes.map(item => ({
      nombre: item.nombre || '',
      cargo: item.cargo || '',
      asistio: item.asistio || 'Virtual',
      email: item.email || ''
    })) : []
  };
  
  return cleanData;
};

/**
 * Valida y limpia los datos extraídos por Gemini incluyendo emails
 * @param {Object} data - Datos extraídos
 * @returns {Object} - Datos validados y limpiados
 */
const validateAndCleanDataWithEmail = (data) => {
  const cleanData = {
    tipoActa: data.tipoActa || '',
    fecha: data.fecha || '',
    horaInicio: data.horaInicio || '',
    horaFinal: data.horaFinal || '',
    lugar: data.lugar || '',
    objetivo: data.objetivo || '',
    ordenDia: Array.isArray(data.ordenDia) ? data.ordenDia.map(item => ({
      titulo: item.titulo || '',
      descripcion: item.descripcion || ''
    })) : [],
    compromisos: Array.isArray(data.compromisos) ? data.compromisos.map(item => ({
      compromiso: item.compromiso || '',
      responsable: item.responsable || '',
      fechaCumplimiento: item.fechaCumplimiento || ''
    })) : [],
    asistentes: Array.isArray(data.asistentes) ? data.asistentes.map(item => ({
      nombre: item.nombre || '',
      cargo: item.cargo || '',
      asistio: item.asistio || 'Virtual',
      email: item.email || ''
    })) : [],
    responsable: data.responsable || ''
  };
  
  return cleanData;
};
